import React, { useEffect, useState } from 'react'
import GlobalNavbar from '../components/GlobalNavbar'
import { addToStoreData, getToStoreData, removeToStoreData } from '../plugins/storage';
import SelectGroupModal from '../components/SelectGroupModal';
import * as bootstrap from 'bootstrap/dist/js/bootstrap';
import axios from '../plugins/axios';
import { NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import moment from 'moment';
import Spinner from '../components/Spinner';

const Communicate = () => {

  const [loggedInMessage, setLoggedInMessage] = useState("You are logged in to Communicator");
  const [modalData, setModalData] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [communicateData, setCommunicateData] = useState([]);
  const [selectGroup, setSelectGroup] = useState({});
  const [openReportId, setOpenReportId] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const history = useNavigate();

  useEffect(() => {
    const selectGroupData = getToStoreData("selectedGroup");
    if (selectGroupData) {
      setLoggedInMessage(loggedInMessage + ' Editing Group: ' + JSON.parse(selectGroupData).name);
      setSelectGroup(JSON.parse(selectGroupData));
    }
  }, []);

  // get data from backend with pagination
  const getCommunicateData = (page = 1) => {
    setIsLoading(true);
    axios.get(`/communications/${selectGroup.id}?page=${page}`).then((response) => {
      if (response.data.success) {
        const payload = response.data.meta;
        if (payload && payload) {
          setCurrentPage(payload.current_page);
          setLastPage(payload.last_page);
          setTotalRecords(payload.total);
          setPerPage(payload.per_page);
        }
        setCommunicateData(response.data.data);
      }
    }).catch((error) => {
      console.log(error);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (selectGroup.id) {
      getCommunicateData(1);
      setCurrentPage(1);
    }
  }, [selectGroup]);

  const handlePageChange = (page) => {
    if (page < 1 || page > lastPage) return;
    setCurrentPage(page);
    getCommunicateData(page);
  };

  const handleGroupSelect = (data) => {
    if (!data) return;
    const message = "You are logged in to Communicator";
    setLoggedInMessage(message + ' Editing Group: ' + data.name);
    modalData && modalData.hide();
    setSelectGroup(data);
  };

  const showModal = () => {
    const myModal = new bootstrap.Modal(document.getElementById('group-modal'));
    setModalData(myModal);
    myModal.show();
  };

  const redirectToEditor = () => {
    removeToStoreData("subject");
    removeToStoreData("communicate_data");
    removeToStoreData("communication_uuid");
    history('/editor');
  };

  const handleDuplicate = (communication_uuid) => {
    axios.post(`/duplicate-communication/${communication_uuid}`)
      .then(response => {
        if (response.data.success) {
          getCommunicateData(currentPage);
        }
      })
      .catch(error => {
        console.error('Error duplicate item:', error);
      });
  };

  const handleDelete = (communication_uuid) => {
    Swal.fire({
      title: "Delete",
      text: "Are you sure you want to delete?",
      icon: "warning",
      showCancelButton: true,
      buttonsStyling: false,
      customClass: {
        confirmButton: "common-btn danger-btn",
        cancelButton: "common-btn secondary-btn me-2",
      },
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        axios.post(`/delete-communication/${communication_uuid}`)
          .then(response => {
            if (response.data.success) {
              // If deleting last item on page > 1, go back one page
              const newPage = communicateData.length === 1 && currentPage > 1
                ? currentPage - 1
                : currentPage;
              getCommunicateData(newPage);
            } else {
              setIsLoading(false);
            }
          })
          .catch(error => {
            console.error('Error deleting item:', error);
            setIsLoading(false);
          });
      }
    });
  };

  const handleUpdate = (data) => {
    addToStoreData("communication_uuid", data.uuid);
    addToStoreData("communicate_data", JSON.stringify(data));
    history('/editor');
  };

  // Report types available for CSV export
  const reportTypes = [
    { type: "Open", label: "Open list" },
    { type: "Delivery", label: "Delivery list" },
    { type: "Bounce", label: "Bounced list" },
    { type: "Click", label: "Clicked list" },
  ];

  // Download a CSV report (Send / Delivery / Bounce) for a communication
  const handleExportReport = (communication_uuid, type) => {
    setOpenReportId(null);
    setIsLoading(true);
    axios
      .get(`/export-communication-report/${communication_uuid}/${type}`, { responseType: "blob" })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${type}_report.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(async (error) => {
        let message = "Failed to export the report.";
        // The error body is a Blob because responseType is "blob"
        if (error.response?.data) {
          try {
            const text = await error.response.data.text();
            message = JSON.parse(text).message || message;
          } catch (e) {
            /* keep default message */
          }
        }
        Swal.fire({
          title: "Export report",
          text: message,
          icon: "warning",
          buttonsStyling: false,
          customClass: { confirmButton: "common-btn primary-btn" },
          confirmButtonText: "OK",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Close the report dropdown when clicking anywhere outside of it
  useEffect(() => {
    if (openReportId === null) return;
    const handleClickOutside = (event) => {
      if (!event.target.closest(".report-action")) {
        setOpenReportId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openReportId]);

  // Build visible page numbers (max 5 buttons)
  const getPageNumbers = () => {
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(lastPage, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <>
      <Wrapper>
        <GlobalNavbar LoggedIn={loggedInMessage} groupName={selectGroup.name} />
        {selectGroup.id &&
          <div className="container my-4">
            <div className="head-section d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div className="title">
                <h3 className="mb-1 dark_title">{selectGroup.name}</h3>
                <p className="mb-0">Your dispatches</p>
              </div>
              {communicateData && communicateData.length > 0 &&
                <div className="create d-flex gap-2 align-items-center flex-wrap">
                  <input type="button" className="common-btn primary-btn" value="Create new" onClick={redirectToEditor} />
                  <NavLink to="/templates" className="common-btn secondary-btn">Templates</NavLink>
                  <input type="button" className="common-btn secondary-btn" value="Change Group" onClick={showModal} />
                </div>}
            </div>

            {communicateData && communicateData.length < 1 ? (
              <div className="not-found-section d-flex align-items-center justify-content-center flex-column">
                <h3>Good to see you!</h3>
                <div className="row">
                  <div className="col-md-7 mx-auto">
                    <p>Your design lineup is as empty as a library on a Friday night! Time to add some designs to the shelf, don't you think?</p>
                  </div>
                </div>
                <div className="d-flex gap-2 justify-content-center align-items-end">
                  <input type="button" className="common-btn primary-btn" value="Create new" onClick={redirectToEditor} />
                  <NavLink to="/templates" className="common-btn secondary-btn">Templates</NavLink>
                  <input type="button" className="common-btn secondary-btn" value="Change Group" onClick={showModal} />
                  <NavLink to={`https://thevault.exchange/groups/${selectGroup?.slug}/group-events/`} target='_blank' className="text-primary-link ps-2">Old editor</NavLink>
                </div>
              </div>
            ) : (
              <>
                <div className="tabs-section">
                  {communicateData.map((curElem) => (
                    <div className="card-box d-flex justify-content-between p-4 mb-4 gap-2" key={curElem.id}>
                      <div className="card-info flex-grow-1 min-width-0">
                        <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                          <h5 className="mb-0" onClick={() => handleUpdate(curElem)}>{curElem.title}</h5>
                          <div className="d-flex flex-wrap gap-2">
                            {curElem.notification_type && Object.keys(curElem.notification_type).length > 0 ? (
                              Object.entries(curElem.notification_type)
                                .filter(([action]) => action?.toLowerCase() !== "send")
                                .map(([action, count]) => (
                                  <label className="label-title" key={action}>{action} <span>{count}</span></label>
                                ))
                            ) : (
                              <>
                                <label className="label-title">Open <span>0</span></label>
                                <label className="label-title">Click <span>0</span></label>
                              </>
                            )}
                          </div>
                        </div>
                        <p className="mb-0 text-break">
                          <span className="pe-1">{moment(curElem.created_at).format("MMM Do,YYYY HH:mm")}</span>|
                          <span className="px-1" style={{ color: "#696969" }}>Created by:</span>
                          {curElem.created_by_name
                            ? <span>{curElem.created_by_name}</span>
                            : <span><i className="fa-solid fa-minus"></i></span>}
                        </p>
                      </div>
                      <div className="card-action d-flex gap-3">
                        <div className="report-action position-relative">
                          <div
                            className="action-btn"
                            title="Download report"
                            onClick={() => setOpenReportId(openReportId === curElem.id ? null : curElem.id)}
                          >
                            <i className="fa-solid fa-file-csv"></i>
                          </div>
                          {openReportId === curElem.id && (
                            <div className="report-menu">
                              {reportTypes.map((report) => (
                                <button
                                  type="button"
                                  className="report-menu-item"
                                  key={report.type}
                                  onClick={() => handleExportReport(curElem.uuid, report.type)}
                                >
                                  <i className="fa-solid fa-download"></i> {report.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="copy-action action-btn" onClick={() => handleDuplicate(curElem.uuid)}>
                          <i className="fa-regular fa-copy"></i>
                        </div>
                        <div className="delete-action action-btn" onClick={() => handleDelete(curElem.uuid)}>
                          <i className="fa-regular fa-trash-can"></i>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Record info + Pagination side by side */}
                <div className="pagination-row d-flex justify-content-center justify-content-md-between align-items-center mt-4 flex-wrap sm-gap-4 md-mb-0 mb-4 gap-3
                ">
                  {totalRecords > 0 ? (
                    <p className="record-info mb-0">
                      Showing {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, totalRecords)} of {totalRecords} records
                    </p>
                  ) : <span />}

                  <div className="pagination-wrapper d-flex align-items-center gap-1 flex-wrap">
                    <button
                      className="page-btn"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      title="First page"
                    >
                      «
                    </button>
                    <button
                      className="page-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      title="Previous page"
                    >
                      ‹
                    </button>

                    {getPageNumbers().map((page) => (
                      <button
                        key={page}
                        className={`page-btn ${page === currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      className="page-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === lastPage}
                      title="Next page"
                    >
                      ›
                    </button>
                    <button
                      className="page-btn"
                      onClick={() => handlePageChange(lastPage)}
                      disabled={currentPage === lastPage}
                      title="Last page"
                    >
                      »
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        }
        <SelectGroupModal handleGroupSelect={handleGroupSelect} showModal={showModal} />
      </Wrapper>
      {isLoading && <Spinner />}
    </>
  );
};

const Wrapper = styled.div`
  .not-found-section {
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    border: 2px solid var(--primary-color);
    padding: 50px 30px;
  }

  .not-found-section h3 {
    font-weight: 600;
    margin-bottom: 18px;
  }

  .not-found-section p {
    text-align: center;
    color: var(--dark-gray-color);
    margin-bottom: 25px;
    font-size: 18px;
  }

  .btn-Vault {
    font-weight: 500 !important;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }

  .btn-Vault-secondary {
    background: none;
    border: 1px solid var(--primary-color);
    color: #000;
    font-weight: 500;
  }

  .btn-Vault-secondary:hover {
    background: var(--secondary-color);
    border: 1px solid var(--secondary-color);
    color: #fff;
  }

  .text-primary-link {
    color: var(--primary-color);
    text-decoration: underline;
    font-weight: 600;
    font-size: 18px;
  }

  .record-info {
    color: var(--dark-gray-color);
    font-family: "Bukra-Regular";
    font-size: 18px;
  }

  /* Report export dropdown */
  .report-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 10;
    min-width: 170px;
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .report-menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 15px;
    color: #333;
    cursor: pointer;
    transition: all 0.15s ease;

    i {
      color: var(--primary-color);
      font-size: 13px;
    }

    &:hover {
      background: var(--primary-color);
      color: #fff;

      i {
        color: #fff;
      }
    }
  }

  /* Pagination */
  .page-btn {
    min-width: 36px;
    height: 36px;
    padding: 0 10px;
    border: 1px solid #ddd;
    background: #fff;
    border-radius: 6px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    color: #333;
    transition: all 0.2s ease;
    line-height: 1;

    &:hover:not(:disabled) {
      background: var(--primary-color);
      border-color: var(--primary-color);
      color: #fff;
    }

    &.active {
      background: var(--primary-color);
      border-color: var(--primary-color);
      color: #fff;
      pointer-events: none;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
`;

export default Communicate;